import { useState, useMemo } from "react";
import { Table, Button, Form, Tabs, Tab } from "react-bootstrap";
import { FaUndo, FaTrash } from "react-icons/fa";
import { useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import SummaryHeader from "../../components/SummaryHeader.jsx";

import {
    useToggleAcceptItemMutation,
    useDeleteOrderItemMutation,
    useUndoRemoveItemMutation,
    useGetUndeliveredOrdersByUserQuery,
    useAcceptOrderItemMutation,
} from "../../slices/ordersApiSlice";
import { toast } from "react-toastify";

const UserItemsScreen = () => {
    const { id: userId } = useParams();

    // Fetch ALL orders from backend (OrderScreen does the same)
    const { data: orders, isLoading, error, refetch } = useGetUndeliveredOrdersByUserQuery(userId);
    const [acceptOrderItem] = useAcceptOrderItemMutation();

    const [activeTab, setActiveTab] = useState("items");
    const [search, setSearch] = useState("");
    const [editedPrices, setEditedPrices] = useState({});

    // --- MUTATIONS (identical to OrderScreen)
    const [toggleAccept] = useToggleAcceptItemMutation();
    const [deleteItem] = useDeleteOrderItemMutation();
    const [undoItem] = useUndoRemoveItemMutation();

    const undeliveredOrders = useMemo(() => orders || [], [orders]);

    // --- MERGE ITEMS ACROSS ORDERS
    const mergedItems = useMemo(() => {
        if (!undeliveredOrders?.length) return [];

        return undeliveredOrders.flatMap((order) =>
            order.orderItems.map((item) => ({
                ...item,
                orderId: order._id,
                date: order.createdAt?.substring(0, 10),
            }))
        );
    }, [undeliveredOrders]);


    // --- SEARCH
    const filteredItems = useMemo(() => {
        let list = [...mergedItems];
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(
                (i) =>
                    i.title?.toLowerCase().includes(q) ||
                    i.isbn?.toLowerCase().includes(q) ||
                    i.orderId.toLowerCase().includes(q)
            );
        }
        return list;
    }, [mergedItems, search]);

    // const deletedItemsCount = useMemo(() => {
    //     return filteredItems.filter((i) => i.isDeleted).length;
    // }, [filteredItems]);

    const activeItems = useMemo(() => {
        if (activeTab === "deleted") {
            return filteredItems.filter((i) => i.isDeleted);
        }

        // default tab → unaccepted & not deleted
        return filteredItems.filter(
            (i) => !i.isDeleted && !i.isAccepted
        );
    }, [filteredItems, activeTab]);

    const userName = undeliveredOrders[0]?.user?.name || 'Onbekend';

    const removedItems = useMemo(() => {
        if (!undeliveredOrders?.length) return [];

        return undeliveredOrders.flatMap(order =>
            (order.removedItems || []).map(item => ({
            ...item,
            orderId: order._id,
            date: order.createdAt?.substring(0, 10),
            }))
        );
    }, [undeliveredOrders]);

    const totalRemoved = removedItems?.length || 0;
    const removedItemsPrice = removedItems?.reduce(
        (acc, item) => acc + Number(item.accepted_price ?? item.offer_price ?? item.price ?? 0),
        0
    );

    // --- SUMMARY HEADER (matches OrderScreen)
    const summary = useMemo(() => {
        const totalItems = mergedItems.length;

        return {
            totalPaid: undeliveredOrders.reduce(
                (s, o) => s + (o.isPaid ? Number(o.itemsPrice) : 0),
                0
            ),
            totalUnpaid: undeliveredOrders.reduce(
                (s, o) => s + (!o.isPaid ? Number(o.itemsPrice) : 0),
                0
            ),
            totalBooks: totalItems,
            totalOrders: undeliveredOrders.length,
            deliveredCount: undeliveredOrders.filter((o) => o.isDelivered).length,
            userCount: totalItems - totalRemoved,
            totalRemoved,
            deletedItemsPrice: removedItemsPrice
        };
    }, [undeliveredOrders, mergedItems, totalRemoved, removedItemsPrice]);

    // --- ACTION HANDLERS
    const handleToggleAccept = async (orderId, itemId) => {
        try {
            await toggleAccept({ orderId, itemId }).unwrap();
            refetch();
        } catch {
            toast.error("Kon accepteren niet wijzigen");
        }
    };

    const handleDeleteItem = async (orderId, itemId) => {
        try {
            await deleteItem({ orderId, itemId }).unwrap();
            refetch();
        } catch {
            toast.error("Kon item niet verwijderen");
        }
    };

    const handleUndoItem = async (orderId, itemId) => {
        try {
            await undoItem({ orderId, removedItemId: itemId }).unwrap();
            refetch();
        } catch {
            toast.error("Herstellen mislukt");
        }
    };

    const handleAcceptItem = async (orderId, itemId, newPrice) => {
        try {
            await acceptOrderItem({ orderId, itemId, accepted_price: newPrice }).unwrap();
            toast.success("Item accepted successfully");
            refetch();
        } catch (err) {
            toast.error(err?.data?.message || "Error accepting item");
        }
    };

    return (
        <>
            <h1 className="display-6 text-center">Boeken van <strong>{userName}</strong></h1>
            <div className="display-9 display-md-5 text-md-end">ID: {userId}</div>

            <SummaryHeader {...summary} />

            {isLoading ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">{error?.data?.message}</Message>
            ) : (
                <>
                    {/* --- SEARCH --- */}
                    <div className="my-3 mx-auto" style={{ maxWidth: "600px" }}>
                        <Form.Control
                            placeholder="Zoek op ISBN, Titel of Order ID..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                                borderRadius: "12px",
                                padding: "12px 16px",
                                border: "2px solid #03337c",
                                boxShadow: "0 0 10px rgba(3,96,86,0.4)",
                            }}
                        />
                    </div>
                        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                            <Tab eventKey="items" title={`Items (${activeItems.length})`}>
                                { activeItems.length === 0 ? (
                                    <Message variant="info">Geen items gevonden om te accepteren.</Message>
                                ) : (
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                {/* <th>Order</th> */}
                                                <th>Datum</th>
                                                <th>ISBN</th>
                                                <th>Titel</th>
                                                <th>Author</th>
                                                <th>Prijs</th>
                                                <th>Accept</th>
                                                <th>Partially Accept</th>
                                                <th>Delete</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {activeItems.map((item) => (
                                                <tr
                                                    key={item._id}
                                                >
                                                    {/* <td>{item.orderId}</td> */}
                                                    <td>{item.date}</td>
                                                    <td>{item.isbn}</td>
                                                    <td>{item.title}</td>
                                                    <td>{item.author}</td>
                                                    <td>
                                                    {item.accepted_price != null && item.accepted_price !== item.offer_price ? (
                                                        <>
                                                        <span style={{ textDecoration: 'line-through', color: '#999' }}>
                                                            €{item.offer_price.toFixed(2)}
                                                        </span>{" "}
                                                        → €{item.accepted_price.toFixed(2)}
                                                        </>
                                                    ) : (
                                                        <>€{item.offer_price.toFixed(2)}</>
                                                    )}
                                                    </td>
                                                    <td>
                                                        <label className="switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={item.isAccepted || false}
                                                            onChange={() => handleToggleAccept(item.orderId, item._id)}
                                                        />
                                                        <span className="slider"></span>
                                                        </label>
                                                    </td>

                                                    <td>
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            <Form.Control
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={
                                                                    editedPrices[item._id] ??
                                                                    item.accepted_price ??
                                                                    item.offer_price
                                                                }
                                                                onChange={(e) =>
                                                                    setEditedPrices((prev) => ({
                                                                    ...prev,
                                                                    [item._id]: Number(e.target.value),
                                                                    }))
                                                                }
                                                                disabled={item.isAccepted}
                                                                size="sm"
                                                                style={{ width: '65px' }}
                                                            />
                                                            <Button
                                                                size="sm"
                                                                disabled={item.isAccepted}
                                                                onClick={() =>
                                                                    handleAcceptItem(
                                                                    item.orderId,
                                                                    item._id,
                                                                    editedPrices[item._id]
                                                                    )
                                                                }
                                                            >
                                                            ✓
                                                            </Button>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <Button
                                                        variant="outline-danger"
                                                        onClick={() => handleDeleteItem(item.orderId, item._id)}
                                                        >
                                                        <FaTrash style={{ fontSize: "18px" }} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Tab>
                            <Tab eventKey="deleted" title={`Deleted Items (${totalRemoved || 0})`}>
                                {totalRemoved === 0 ? (
                                    <Message variant="warning">Geen deleted items gevonden.</Message>
                                ) : <Table striped bordered hover responsive>
                                    <thead>
                                        <tr>
                                            {/* <th>Order</th> */}
                                            <th>Datum</th>
                                            <th>ISBN</th>
                                            <th>Titel</th>
                                            <th>Author</th>
                                            <th>Prijs</th>
                                            <th>Herstellen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {removedItems.map((item) => (
                                            <tr
                                                key={item._id}
                                            >
                                                {/* <td>{item.orderId}</td> */}
                                                <td>{item.date}</td>
                                                <td>{item.isbn}</td>
                                                <td>{item.title}</td>
                                                <td>{item.author}</td>
                                                <td>€ {Number(item.accepted_price ?? item.offer_price ?? item.price ?? 0)}</td>

                                                <td>
                                                    <button
                                                    className="undo-btn"
                                                    onClick={() => handleUndoItem(item.orderId, item._id)}
                                                    >
                                                    <FaUndo /> Undo
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>}
                            </Tab>
                        </Tabs>
                    <style>
                    {`
                    .order-summary-card {
                        background: linear-gradient(to bottom right, #e0e9f5ff, #f7f9ff);
                        border: 1px solid #e4e7f0;
                    }

                    .summary-block {
                        transition: transform 0.15s ease, background-color 0.25s ease;
                        padding: 10px;
                        border-radius: 10px;
                    }

                    .summary-block:hover {
                        background-color: #f5f7ff;
                        transform: translateY(-3px);
                    }

                    .separator {
                        width: 1px;
                        height: 40px;
                        background-color: #0b0a0aff;
                        margin: auto;
                    }

                    .action-button {
                        font-size: 1rem;
                        font-weight: 600;
                        min-height: 45px;
                        border-radius: 10px !important;
                        transition: background-color 0.2s ease, transform 0.15s ease;
                    }

                    .action-button:hover {
                        transform: translateY(-2px);
                    }

                    .deliver-btn {
                        background-color: #0d6efd;
                        border-color: #1b6be2ff;
                    }
                    .deliver-btn:hover {
                        background-color: #0b2f64ff;
                    }

                    .paid-btn {
                        background-color: #28a745;
                        border-color: #28a745;
                    }
                    .paid-btn:hover {
                        background-color: #0c6e21ff;
                    }
                    .switch {
                    position: relative;
                    display: inline-block;
                    width: 46px;
                    height: 24px;
                    }

                    .switch input { display: none; }

                    .slider {
                    position: absolute;
                    cursor: pointer;
                    top: 0; left: 0;
                    right: 0; bottom: 0;
                    background-color: #ccc;
                    transition: .4s;
                    border-radius: 34px;
                    }

                    .slider:before {
                    position: absolute;
                    content: "";
                    height: 18px;
                    width: 18px;
                    left: 3px;
                    bottom: 3px;
                    background-color: white;
                    transition: .4s;
                    border-radius: 50%;
                    }

                    input:checked + .slider {
                    background-color: #28a745;
                    }

                    input:checked + .slider:before {
                    transform: translateX(22px);
                    }

                    .undo-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: #0d6efd10;
                    color: #0d6efd;
                    border: 1px solid #0d6efd;
                    padding: 6px 12px;
                    border-radius: 10px;
                    transition: 0.25s ease;
                    }

                    .undo-btn:hover {
                    background: #0d6efd;
                    color: white;
                    transform: scale(1.05);
                    }
                    `}
                    </style>
                </>
            )}
        </>
    );
};

export default UserItemsScreen;